import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/auth/reset-password';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type DoResetPasswordInput = paths[url]['post']['requestBody']['content']['application/json'];

/**
 * Requests endpoint to authenticate user.
 */
export const useCompleteResetPassword = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const sessionId = getSessionId();

    const request = async (props: DoResetPasswordInput) => {
        setLoading(true);
        setSuccess(null);
        setError(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...props, session_id: sessionId }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.message || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data = await response.json();

            setSuccess(data.message);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, error, lastResponseTime };
};
